import { useEffect, useRef } from 'react'

/**
 * Cuánto se muestra el acuse antes de avanzar solo al paso siguiente
 * (en la tablet: el encadenado al próximo cuestionario o el check-in; en
 * `/data`: lo que haga el post-submit del flujo personal).
 */
export const SUBMIT_SUCCESS_MS = 3000

/**
 * Acuse de envío del cuestionario (pedido de mg, 2026-09-18: hasta ahora el
 * jugador tocaba "Enviar respuestas" y la pantalla cambiaba sin decirle que
 * el POST había salido bien).
 *
 * Se monta cuando la respuesta ya está guardada en el backend, así que el
 * tick no promete nada que todavía no pasó. A los `SUBMIT_SUCCESS_MS` llama
 * `onDone`, que es lo que hasta acá corría inmediatamente después del POST:
 * el contrato con `TabletLayout` (`state.justSubmitted` en la history entry)
 * no cambia, sólo se retrasa.
 */
export function SubmitSuccess({
  heading,
  name,
  onDone,
}: {
  heading: string
  /** Nombre del cuestionario, para que el acuse diga QUÉ se envió. */
  name: string
  onDone: () => void
}) {
  // `onDone` viene como arrow inline del padre: si el timer dependiera de su
  // identidad, cualquier re-render reiniciaría los 3 s.
  const doneRef = useRef(onDone)
  doneRef.current = onDone
  useEffect(() => {
    const t = window.setTimeout(() => doneRef.current(), SUBMIT_SUCCESS_MS)
    return () => window.clearTimeout(t)
  }, [])

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-0 z-[60] flex flex-col items-center justify-center gap-lg bg-surface p-lg text-center"
    >
      <svg
        viewBox="0 0 120 120"
        aria-hidden="true"
        className="h-32 w-32 sm:h-40 sm:w-40"
      >
        <circle cx="60" cy="60" r="52" className="fill-goal/10 stroke-none" />
        <circle
          cx="60"
          cy="60"
          r="52"
          strokeWidth="6"
          strokeLinecap="round"
          className="tick-ring fill-none stroke-goal"
        />
        <path
          d="M37 62 L53 78 L84 44"
          strokeWidth="8"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="tick-check fill-none stroke-goal"
        />
      </svg>
      <h1 className="m-0 text-2xl font-bold tracking-tight text-text-primary sm:text-3xl">
        {heading}
      </h1>
      <p className="m-0 max-w-xl text-lg text-text-secondary">{name}</p>
    </div>
  )
}
